<template>
    <header
        id="header"
        class="w-full min-h-14 z-10 bg-black border-b border-gray-700"
    >
        <slot name="top-header"></slot>

        <div class="header-menu relative h-14 flex items-center px-6 justify-center small_desktop:justify-between mobile:px-4">
            <button
                class="absolute left-4 top-3 w-8 h-8 small_desktop:hidden"
                type="button"
                @click="openMobileNav"
            >
                <BaseIcon
                    icon-name="icon-circle-help"
                    icon-color="#FFFFFF"
                >
                    <IconMenu />
                </BaseIcon>
            </button>

            <router-link
                :to="{ name: 'home' }"
                class="text-none"
            >
                <IconYampiPartnersNegative class="h-5" />
            </router-link>

            <div class="hidden items-center h-full small_desktop:flex">
                <ul class="flex gap-2 list-none m-0 p-0 items-center">
                    <li class="relative">
                        <a
                            class="ak-trigger hover:bg-glass-high_light"
                            href="javascript:"
                        >
                            <div
                                class="text-none"
                                v-tooltip.top="{
                                    content: 'Releases',
                                    html: true,
                                }"
                            >
                                <BaseIcon
                                    icon-name="icon-circle-help"
                                    icon-color="#ECECEE"
                                >
                                    <IconSubscribeNotification />
                                </BaseIcon>
                            </div>
                        </a>
                        <AnnounceKit
                            catchClick=".ak-trigger"
                            widget="https://releases.yampi.com.br/widgets/v2/1shgvC"
                            :user="{
                                id: authUser.data?.id.toString(),
                                email: authUser.data?.email,
                                name: authUser.data?.name,
                            }"
                            :labels="['parceiro desenvolvedor']"
                            :widget-style="akStyle"
                        />
                    </li>
                    <li class="relative">
                        <a
                            :href="
                                userIsAffiliate
                                    ? 'https://help.yampi.com.br/pt-BR/collections/3385839-afiliados'
                                    : 'https://help.yampi.com.br/pt-BR/collections/3615940-especialistas'
                            "
                            target="_blank"
                            class="h-8 w-8"
                        >
                            <div
                                class="text-none"
                                v-tooltip.top="{
                                    content: 'Ajuda',
                                    html: true,
                                }"
                            >
                                <BaseIcon
                                    icon-name="icon-circle-help"
                                    icon-color="#ECECEE"
                                >
                                    <IconCircleHelp />
                                </BaseIcon>
                            </div>
                        </a>
                    </li>
                    <li class="relative">
                        <button
                            id="user-menu"
                            :class="{ 'shadow-07 outline-0 ring-0': isShown }"
                            class="w-8 h-8 rounded-full flex items-center justify-center hover:shadow-07 focus:ring-0 focus:outline-0 transition"
                            aria-label="User menu"
                            aria-haspopup="true"
                            @click.stop="toggleUserMenuDropdown"
                            @mousedown="mousedownHandler"
                            @focus="focusHandler"
                            @blur="blurHandler"
                            @keydown="keydownHandler"
                        >
                            <img
                                width="30"
                                height="30"
                                src="../../assets/img/svg/no-user-icon.svg"
                                alt="Usuário sem foto"
                            />
                        </button>
                        <UserMenuDropdown
                            :visible="isShown"
                            :name="authUser.data?.name"
                            :socialName="authUser.data?.social_name"
                            :email="authUser.data?.email"
                            :user-ref="userRef"
                            @close="handleClose"
                        />
                    </li>
                </ul>
            </div>
        </div>
    </header>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import AnnounceKit from '../AnnounceKit.vue';

// Mixins
import auth from '@/mixins/auth';
import trackingSDK from '@/mixins/tracking/sdk';

// Composables
import { useClarity } from '@/composables/useClarity';

// Icons
import BaseIcon from '@/icons/BaseIcon';
import IconMenu from '@/icons/IconMenu';
import IconSubscribeNotification from '@/icons/IconSubscribeNotification';
import IconCircleHelp from '@/icons/IconCircleHelp';
import UserMenuDropdown from '@/components/UserMenuDropdown.vue';
import IconYampiPartnersNegative from '@/icons/YpIcons/IconYampiPartnersNegative.vue';

// Keep mixins using defineOptions
defineOptions({
    name: 'Header',
    mixins: [auth, trackingSDK],
});

const store = useStore();
const route = useRoute();
const { clarityIdentify } = useClarity();

// State
const showProfileDropdown = ref(false);
const isShown = ref(false);
const akStyle = {
    position: 'absolute',
    bottom: '100%',
    left: '100%',
    fontSize: '16px',
    transform: 'translate(100%, -100%)',
};

// Methods
const toggleMobileMenu = () => store.dispatch('navbar/toggleMobileMenu');

const toggleUserMenuDropdown = () => {
    isShown.value = !isShown.value;
};

const openMobileNav = () => {
    const html = document.querySelector('html');
    html.classList.add('slide-nav-active');
    toggleMobileMenu();
};

const handleClose = () => {
    isShown.value = false;
};

const keydownHandler = (event) => {
    if (event.key === 'Escape') {
        handleClose();
    }
};

// Computed
const userIsAffiliate = computed(() => store.getters['profile/userIsAffiliate']);
const userRef = computed(() => store.getters['profile/userRef']);
const authUser = computed(() => store.getters['auth/user'] || {});

// Watchers
watch(route, () => {
    const html = document.querySelector('html');
    showProfileDropdown.value = false;
    html.classList.remove('slide-nav-active');
});

watch(
    () => authUser.value.data,
    (user) => {
        if (user?.id) {
            clarityIdentify(user.id.toString(), user.name);
        }
    },
    { immediate: true },
);
</script>
